import React, { useMemo, useState } from "react";
import { Slider } from "../components/ui/slider";
import { formatTT } from "../lib/api";
import { Calculator as CalcIcon } from "lucide-react";

const Calculator = () => {
    const [principal, setPrincipal] = useState(100000);
    const [rate, setRate] = useState(8.5);
    const [months, setMonths] = useState(60);

    const calc = useMemo(() => {
        const r = rate / 100 / 12;
        const emi = principal * r * Math.pow(1 + r, months) / (Math.pow(1 + r, months) - 1);
        const total = emi * months;
        const interest = total - principal;
        return { emi, total, interest };
    }, [principal, rate, months]);

    return (
        <div className="max-w-6xl mx-auto px-6 lg:px-12 py-12">
            <div className="mb-10">
                <div className="label-overline text-[#1E3A2B] mb-2 flex items-center gap-2">
                    <CalcIcon className="w-3.5 h-3.5" strokeWidth={1.5}/> Tools
                </div>
                <h1 className="font-serif-display text-4xl md:text-5xl tracking-tight mb-3">Loan Calculator</h1>
                <p className="text-sm text-[#4A5568] max-w-2xl">Estimate your monthly installment, total interest paid, and overall cost. Adjust the sliders to see results update instantly.</p>
            </div>

            <div className="grid lg:grid-cols-5 gap-8">
                {/* Controls */}
                <div className="lg:col-span-3 grid-card bg-white p-8 space-y-8">
                    <div>
                        <div className="flex items-baseline justify-between mb-3">
                            <div className="label-overline">Loan Amount</div>
                            <div className="font-mono-body text-2xl" data-testid="calc-principal-display">{formatTT(principal)}</div>
                        </div>
                        <Slider
                            data-testid="calc-principal-slider"
                            value={[principal]}
                            onValueChange={(v) => setPrincipal(v[0])}
                            min={5000} max={5000000} step={5000}
                        />
                        <div className="flex justify-between mt-1 text-xs text-[#718096] font-mono-body">
                            <span>{formatTT(5000)}</span>
                            <span>{formatTT(5000000)}</span>
                        </div>
                    </div>

                    <div>
                        <div className="flex items-baseline justify-between mb-3">
                            <div className="label-overline">Interest Rate</div>
                            <div className="font-mono-body text-2xl" data-testid="calc-rate-display">{rate.toFixed(2)}%</div>
                        </div>
                        <Slider
                            data-testid="calc-rate-slider"
                            value={[rate]}
                            onValueChange={(v) => setRate(v[0])}
                            min={3} max={25} step={0.25}
                        />
                        <div className="flex justify-between mt-1 text-xs text-[#718096] font-mono-body">
                            <span>3%</span>
                            <span>25%</span>
                        </div>
                    </div>

                    <div>
                        <div className="flex items-baseline justify-between mb-3">
                            <div className="label-overline">Tenure</div>
                            <div className="font-mono-body text-2xl" data-testid="calc-months-display">{months} months ({(months / 12).toFixed(1)} yr)</div>
                        </div>
                        <Slider
                            data-testid="calc-months-slider"
                            value={[months]}
                            onValueChange={(v) => setMonths(v[0])}
                            min={6} max={360} step={6}
                        />
                        <div className="flex justify-between mt-1 text-xs text-[#718096] font-mono-body">
                            <span>6 mo</span>
                            <span>360 mo</span>
                        </div>
                    </div>
                </div>

                {/* Results */}
                <div className="lg:col-span-2 space-y-4">
                    <div className="grid-card bg-[#0B192C] text-[#F8F7F4] p-8">
                        <div className="label-overline opacity-70 mb-2">Monthly Payment</div>
                        <div className="font-serif-display text-5xl tracking-tight" data-testid="calc-emi-result">
                            {formatTT(calc.emi)}
                        </div>
                        <div className="text-xs opacity-70 mt-1 font-mono-body">over {months} months</div>
                    </div>

                    <div className="grid-card bg-white p-6">
                        <div className="label-overline text-[#718096] mb-1">Total Interest</div>
                        <div className="font-mono-body text-2xl" data-testid="calc-interest-result">{formatTT(calc.interest)}</div>
                    </div>

                    <div className="grid-card bg-white p-6">
                        <div className="label-overline text-[#718096] mb-1">Total Repayment</div>
                        <div className="font-mono-body text-2xl" data-testid="calc-total-result">{formatTT(calc.total)}</div>
                    </div>

                    <div className="grid-card bg-[#EAE7E1] p-5">
                        <div className="text-xs text-[#4A5568] leading-relaxed">
                            Reduce your tenure or boost the principal to shrink your total interest. A 12-month reduction on a TT$100,000 loan can save thousands.
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Calculator;
